import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Camera, CheckCircle2, Loader2, Sparkles, Copy, Download, Smartphone, User as UserIcon, MapPin, Package } from "lucide-react";
import { maskCPF, maskPhone, maskCEP, maskDate, isoFromBRDate, lookupCEP, unmask } from "@/lib/masks";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";
import MpPixDialog from "@/components/MpPixDialog";
import MpCardDialog from "@/components/MpCardDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API });

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };

const STEPS = [
  { key: "dados", label: "Seus dados", icon: UserIcon },
  { key: "endereco", label: "Endereço", icon: MapPin },
  { key: "plano", label: "Plano", icon: Package },
  { key: "pix", label: "Pagamento", icon: Sparkles },
  { key: "facial", label: "Foto", icon: Camera },
];

export default function SelfSignup() {
  const { unitId } = useParams();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    name: "", cpf: "", email: "", phone: "", birthdate: "", gender: "masculino",
    zip_code: "", address: "", address_number: "", address_complement: "",
    neighborhood: "", city: "", state: "",
    unit_id: "", plan_id: "", portal_password: "",
  });
  const [captured, setCaptured] = useState(null);
  const [modelsReady, setModelsReady] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [mpPixOpen, setMpPixOpen] = useState(false);
  const [mpCardOpen, setMpCardOpen] = useState(false);
  const [payMethod, setPayMethod] = useState(null); // 'pix' | 'card'
  const [mpSessionId, setMpSessionId] = useState(null);
  const [createdStudent, setCreatedStudent] = useState(null);
  const videoRef = useRef(null); const canvasRef = useRef(null); const streamRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/self-signup/config", { params: unitId ? { unit_id: unitId } : {} });
        setUnits(data.units || []);
        setPlans(data.plans || []);
        if (unitId) setForm((f) => ({ ...f, unit_id: unitId }));
        else if ((data.units || []).length === 1) setForm((f) => ({ ...f, unit_id: data.units[0].id }));
      } catch { toast.error("Falha carregando dados"); }
    })();
  }, [unitId]);

  useEffect(() => {
    if (step === 4 && !modelsReady) loadFaceModels().then(() => setModelsReady(true)).catch(() => toast.error("Falha carregando face-api"));
    return () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // PWA: swap to totem manifest when autocadastro is aberto pelo totem
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/totem-manifest.webmanifest");
    return () => { if (link && original) link.setAttribute("href", original); };
  }, []);

  // Auto-return timer on success step (8s) — para o totem já estar pronto pra próximo cliente
  const [returnCountdown, setReturnCountdown] = useState(8);
  useEffect(() => {
    if (step !== 5) return;
    setReturnCountdown(8);
    const iv = setInterval(() => {
      setReturnCountdown((c) => {
        if (c <= 1) {
          clearInterval(iv);
          window.location.href = unitId ? `/totem/${unitId}` : "/totem";
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Attach camera stream to video element AFTER it renders on screen.
  // We can't set srcObject inside startCam because the <video> only mounts
  // when streaming=true — before that, videoRef.current is null.
  useEffect(() => {
    if (streaming && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [streaming]);

  const onCep = async (v) => {
    const m = maskCEP(v);
    setForm((f) => ({ ...f, zip_code: m }));
    if (unmask(m).length === 8) {
      const found = await lookupCEP(m);
      if (found) {
        setForm((f) => ({ ...f, address: found.address || f.address, neighborhood: found.neighborhood || f.neighborhood, city: found.city || f.city, state: found.state || f.state }));
        toast.success("Endereço preenchido");
      } else { toast.error("CEP não encontrado"); }
    }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim() || form.name.trim().split(" ").length < 2) return toast.error("Digite seu nome completo") || false;
      if (unmask(form.cpf).length !== 11) return toast.error("CPF inválido") || false;
      if (!form.phone || unmask(form.phone).length < 10) return toast.error("Telefone/WhatsApp obrigatório") || false;
      if (!form.portal_password || form.portal_password.length < 4) return toast.error("Crie uma senha (mín 4 caracteres)") || false;
      return true;
    }
    if (step === 1) {
      if (!form.zip_code || !form.address || !form.city) return toast.error("Preencha CEP, endereço e cidade") || false;
      return true;
    }
    if (step === 2) {
      if (units.length > 1 && !form.unit_id) return toast.error("Escolha uma unidade") || false;
      if (!form.plan_id) return toast.error("Escolha um plano") || false;
      return true;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const startCam = async () => {
    try {
      // Diagnóstico: verifica se estamos em contexto seguro / com mediaDevices
      if (typeof window === "undefined" || !navigator?.mediaDevices?.getUserMedia) {
        toast.error("Câmera não disponível neste navegador. Use Chrome/Safari no tablet e abra o site em HTTPS.");
        return;
      }
      if (!window.isSecureContext) {
        toast.error("A câmera exige HTTPS. Abra o totem usando o link seguro (https://...).");
        return;
      }
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
      } catch (err1) {
        // Fallback sem constraints estritas — alguns tablets são chatos com facingMode
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      // Mostrar o elemento <video>; o useEffect anexa o srcObject após renderizar
      setStreaming(true);
    } catch (err) {
      const name = err?.name || "";
      let msg = "Não foi possível abrir a câmera.";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = "Permissão negada. Toque no cadeado do navegador → Permissões → Câmera → Permitir, e tente novamente.";
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        msg = "Nenhuma câmera encontrada. Verifique se o tablet tem câmera frontal habilitada.";
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        msg = "A câmera está sendo usada por outro app. Feche outros apps e tente de novo.";
      } else if (name === "OverconstrainedError") {
        msg = "A câmera não suporta essa resolução — tentando fallback automático.";
      } else if (err?.message) {
        msg = `Erro na câmera: ${err.message}`;
      }
      toast.error(msg);
      console.error("startCam error:", err);
    }
  };
  const stopCam = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; setStreaming(false); };
  const capture = async () => {
    if (!modelsReady) return toast.error("Modelos carregando, aguarde...");
    const video = videoRef.current, canvas = canvasRef.current;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
    const desc = await detectEnrollFace(video);
    if (desc.problem) return toast.error(desc.problem);
    setCaptured({ photo: dataUrl, descriptor: desc.descriptor });
    stopCam();
    toast.success("Foto capturada!");
  };

  const submit = async () => {
    // Novo fluxo: avança para seleção de método (Pix / Cartão) no step 3
    setStep(3);
    setPayMethod(null);
    setMpPixOpen(false);
    setMpCardOpen(false);
  };

  const onMpPaid = (chargeInfo) => {
    // chargeInfo tem external_reference e student_id (vindo do status)
    const extRef = chargeInfo?.external_reference;
    const sid = chargeInfo?.student_id;
    if (extRef) setMpSessionId(`mp_${extRef}`);
    if (sid) setCreatedStudent({ id: sid, cpf: unmask(form.cpf), name: form.name.trim() });
    setStep(4); // ir para foto após pagamento
  };

  const submitFace = async () => {
    if (!captured) return toast.error("Capture sua foto antes de continuar");
    if (!mpSessionId) return toast.error("Sessão inválida");
    setLoading(true);
    try {
      await http.post(`/self-signup/attach-face/${mpSessionId}`, {
        face_photo: captured.photo,
        face_embedding: captured.descriptor,
      });
      setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar foto"); }
    finally { setLoading(false); }
  };
  const skipFace = () => setStep(5);

  const goPortal = () => {
    if (!createdStudent) return;
    window.location.href = `/portal/login?cpf=${createdStudent.cpf}`;
  };

  const CurrentStepBadge = () => (
    <div className="flex items-center gap-2 justify-center flex-wrap mb-8" data-testid="signup-steps">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all
              ${done ? "bg-emerald-500 text-white" : active ? "bg-rose-600 text-white ring-4 ring-rose-500/30" : "bg-white/10 text-white/40"}`}>
              {done ? <CheckCircle2 className="w-4 h-4"/> : <Icon className="w-4 h-4"/>}
            </div>
            {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${done ? "bg-emerald-500" : "bg-white/10"}`}/>}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-[100dvh] max-h-[100dvh] overflow-y-auto bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white">
      <header className="px-4 md:px-10 py-4 md:py-6 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png" alt="American Fit" className="h-8 md:h-10"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-rose-400 font-bold">Autocadastro</div>
            <div className="font-heading text-lg md:text-xl font-black uppercase leading-tight">Novo Aluno</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-4 md:py-8 pb-24">
        {step < 5 && <CurrentStepBadge />}

        {/* Step 0 — Dados pessoais */}
        {step === 0 && (
          <Card className="p-6 md:p-8 bg-white/5 border-white/10 backdrop-blur" data-testid="signup-step-dados">
            <h1 className="font-heading text-3xl font-black uppercase mb-2 text-rose-500">Seus dados</h1>
            <p className="text-white/60 mb-6">Preencha as informações básicas para começar seu cadastro.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label className="text-white/80">Nome completo *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: João da Silva" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-name"/></div>
              <div><Label className="text-white/80">CPF *</Label><Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} placeholder="000.000.000-00" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-cpf"/></div>
              <div><Label className="text-white/80">WhatsApp *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} placeholder="(11) 99999-9999" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-phone"/></div>
              <div><Label className="text-white/80">E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@email.com" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-email"/></div>
              <div><Label className="text-white/80">Nascimento</Label><Input inputMode="numeric" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: maskDate(e.target.value) })} placeholder="DD/MM/AAAA" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-birthdate"/></div>
              <div><Label className="text-white/80">Gênero</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="signup-gender"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem><SelectItem value="outro">Outro</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label className="text-white/80">Crie uma senha para o portal *</Label><Input type="password" value={form.portal_password} onChange={(e) => setForm({ ...form, portal_password: e.target.value })} placeholder="Mínimo 4 caracteres" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-password"/><p className="text-xs text-white/40 mt-1">Você usará essa senha + seu CPF para acessar o app do aluno.</p></div>
            </div>
          </Card>
        )}

        {/* Step 1 — Endereço */}
        {step === 1 && (
          <Card className="p-6 md:p-8 bg-white/5 border-white/10 backdrop-blur" data-testid="signup-step-endereco">
            <h1 className="font-heading text-3xl font-black uppercase mb-2 text-rose-500">Endereço</h1>
            <p className="text-white/60 mb-6">Digite o CEP e preencheremos o resto pra você.</p>
            <div className="grid md:grid-cols-6 gap-4">
              <div className="md:col-span-2"><Label className="text-white/80">CEP *</Label><Input value={form.zip_code} onChange={(e) => onCep(e.target.value)} placeholder="00000-000" className="bg-slate-950 border-slate-700 text-white" data-testid="signup-cep"/></div>
              <div className="md:col-span-4"><Label className="text-white/80">Logradouro *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-950 border-slate-700 text-white" data-testid="signup-address"/></div>
              <div className="md:col-span-2"><Label className="text-white/80">Número</Label><Input value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} className="bg-slate-950 border-slate-700 text-white" data-testid="signup-number"/></div>
              <div className="md:col-span-4"><Label className="text-white/80">Complemento</Label><Input value={form.address_complement} onChange={(e) => setForm({ ...form, address_complement: e.target.value })} className="bg-slate-950 border-slate-700 text-white"/></div>
              <div className="md:col-span-3"><Label className="text-white/80">Bairro</Label><Input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} className="bg-slate-950 border-slate-700 text-white" data-testid="signup-neighborhood"/></div>
              <div className="md:col-span-2"><Label className="text-white/80">Cidade *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-slate-950 border-slate-700 text-white" data-testid="signup-city"/></div>
              <div className="md:col-span-1"><Label className="text-white/80">UF</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} className="bg-slate-950 border-slate-700 text-white" data-testid="signup-state"/></div>
            </div>
          </Card>
        )}

        {/* Step 2 — Planos e unidade */}
        {step === 2 && (
          <Card className="p-6 md:p-8 bg-white/5 border-white/10 backdrop-blur" data-testid="signup-step-plano">
            <h1 className="font-heading text-3xl font-black uppercase mb-2 text-rose-500">Escolha seu plano</h1>
            <p className="text-white/60 mb-6">O primeiro pagamento é feito agora via Pix. Depois você renova pelo próprio app.</p>
            {units.length > 1 && !unitId && (
              <div className="mb-6"><Label className="text-white/80">Unidade *</Label>
                <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="signup-unit"><SelectValue placeholder="Selecione uma unidade"/></SelectTrigger>
                  <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.unit_id && (unitId || units.length === 1) && (
              <div className="mb-6 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3" data-testid="signup-unit-locked">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-emerald-400"/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Unidade</div>
                  <div className="font-heading text-lg font-black uppercase">{units.find(u => u.id === form.unit_id)?.name || "—"}</div>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {plans.map((p) => {
                const selected = form.plan_id === p.id;
                return (
                  <button key={p.id} onClick={() => setForm({ ...form, plan_id: p.id })} data-testid={`signup-plan-${p.id}`}
                    className={`text-left p-5 rounded-2xl border-2 transition-all ${selected ? "border-rose-500 bg-rose-950/40 shadow-[0_10px_40px_-10px_rgba(225,29,72,0.6)]" : "border-white/10 bg-white/5 hover:border-rose-500/50"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-heading text-xl font-black uppercase text-rose-500">{p.name}</div>
                      {selected && <CheckCircle2 className="w-6 h-6 text-rose-400"/>}
                    </div>
                    <div className="text-sm text-white/60 mb-3 min-h-[40px]">{p.description || `${p.duration_days ? `${p.duration_days} dias` : `${p.duration_months} mês(es)`} de acesso`}</div>
                    <div className="text-3xl font-heading font-black text-white">{money(p.price)}</div>
                    <div className="text-xs text-white/40 mt-1">{p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`}</div>
                  </button>
                );
              })}
              {plans.length === 0 && <div className="col-span-2 py-12 text-center text-white/50">Nenhum plano disponível ainda — procure a recepção.</div>}
            </div>
          </Card>
        )}

        {/* Step 3 — Escolha do método + Dialogs (Pix ou Cartão via Mercado Pago) */}
        {step === 3 && (
          <Card className="p-6 md:p-8 bg-white/5 border-white/10 backdrop-blur" data-testid="signup-step-pix">
            <h1 className="font-heading text-3xl font-black uppercase mb-2 text-rose-500">Pagamento</h1>
            <p className="text-white/60 mb-6">Escolha a forma de pagamento. A confirmação é automática — depois você tira a foto para liberar sua catraca.</p>

            {payMethod === null && (
              <div className="grid md:grid-cols-2 gap-3" data-testid="signup-pay-methods">
                <button
                  onClick={() => { setPayMethod("pix"); setMpPixOpen(true); }}
                  className="p-5 rounded-2xl border-2 border-white/10 bg-white/5 hover:border-rose-500 hover:bg-rose-950/30 transition active:scale-95 text-left"
                  data-testid="signup-pay-method-pix">
                  <div className="w-12 h-12 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mb-3">
                    <Sparkles className="w-6 h-6 text-rose-400"/>
                  </div>
                  <div className="font-heading text-xl font-black uppercase text-white">Pix</div>
                  <div className="text-xs text-white/60 mt-1">Aprovação em segundos · sem taxa extra</div>
                </button>
                <button
                  onClick={() => { setPayMethod("card"); setMpCardOpen(true); }}
                  className="p-5 rounded-2xl border-2 border-white/10 bg-white/5 hover:border-emerald-500 hover:bg-emerald-950/20 transition active:scale-95 text-left"
                  data-testid="signup-pay-method-card">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                    <Package className="w-6 h-6 text-emerald-400"/>
                  </div>
                  <div className="font-heading text-xl font-black uppercase text-white">Cartão de crédito</div>
                  <div className="text-xs text-white/60 mt-1">
                    Até <strong>{Math.max(1, plans.find(p => p.id === form.plan_id)?.max_installments || 1)}x</strong> no cartão
                  </div>
                </button>
              </div>
            )}

            {payMethod !== null && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 text-rose-300 py-8">
                  <Loader2 className="w-6 h-6 animate-spin"/>
                  <span className="uppercase text-sm tracking-widest font-bold" data-testid="signup-pix-waiting">
                    {payMethod === "pix" ? "Aguardando pagamento Pix..." : "Processando cartão..."}
                  </span>
                </div>
                {payMethod === "pix" && !mpPixOpen && (
                  <Button onClick={() => setMpPixOpen(true)} className="bg-rose-600 hover:bg-rose-700 font-black uppercase mt-2" data-testid="signup-pix-reopen">
                    Reabrir QR Code
                  </Button>
                )}
                {payMethod === "card" && !mpCardOpen && (
                  <Button onClick={() => setMpCardOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 font-black uppercase mt-2" data-testid="signup-card-reopen">
                    Reabrir formulário do cartão
                  </Button>
                )}
                <Button variant="ghost" onClick={() => { setPayMethod(null); setMpPixOpen(false); setMpCardOpen(false); }} className="text-white/50 hover:text-white block mx-auto mt-4 underline text-xs" data-testid="signup-change-method">
                  Trocar forma de pagamento
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step 4 — Facial (after payment) */}
        {step === 4 && (
          <Card className="p-6 md:p-8 bg-white/5 border-white/10 backdrop-blur text-center" data-testid="signup-step-facial">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400"/>
            </div>
            <h1 className="font-heading text-3xl font-black uppercase mb-2 text-rose-500">Pagamento confirmado!</h1>
            <p className="text-white/60 mb-6">Agora tire uma foto para liberar sua entrada na catraca. Olhe direto para a câmera e mantenha o rosto centralizado.</p>
            <div className="mx-auto max-w-md">
              {captured ? (
                <div>
                  <img src={captured.photo} alt="Você" className="w-64 h-64 mx-auto rounded-2xl object-cover border-4 border-emerald-500" data-testid="signup-captured-img"/>
                  <div className="mt-4 flex items-center justify-center gap-2 text-emerald-400"><CheckCircle2 className="w-5 h-5"/><span className="font-bold uppercase text-sm">Foto capturada</span></div>
                  <div className="mt-4 flex gap-2 justify-center flex-wrap">
                    <Button variant="outline" onClick={() => { setCaptured(null); }} className="bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="signup-recapture"><Camera className="w-4 h-4 mr-2"/>Refazer</Button>
                    <Button onClick={submitFace} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 font-black uppercase" data-testid="signup-face-submit">
                      {loading ? <Loader2 className="animate-spin w-4 h-4 mr-2"/> : <ArrowRight className="w-4 h-4 mr-2"/>}Concluir cadastro
                    </Button>
                  </div>
                </div>
              ) : streaming ? (
                <div>
                  <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md mx-auto rounded-2xl border-4 border-rose-500 face-frame"/>
                  <canvas ref={canvasRef} className="hidden"/>
                  <Button onClick={capture} disabled={!modelsReady} className="mt-4 bg-rose-600 hover:bg-rose-700 font-black uppercase" data-testid="signup-capture">
                    <Camera className="w-4 h-4 mr-2"/>{modelsReady ? "Capturar" : "Carregando..."}
                  </Button>
                </div>
              ) : (
                <div className="py-8">
                  <div className="w-40 h-40 mx-auto rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center mb-4">
                    <Camera className="w-16 h-16 text-white/30"/>
                  </div>
                  <Button onClick={startCam} className="bg-rose-600 hover:bg-rose-700 font-black uppercase" data-testid="signup-start-cam"><Camera className="w-4 h-4 mr-2"/>Abrir câmera</Button>
                  <div className="mt-6">
                    <Button variant="ghost" onClick={skipFace} className="text-white/50 hover:text-white/80 text-xs underline" data-testid="signup-skip-face">Pular por agora — cadastrar rosto depois na recepção</Button>
                  </div>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden"/>
            </div>
          </Card>
        )}

        {/* Step 5 — Sucesso */}
        {step === 5 && (
          <Card className="p-6 md:p-10 bg-white/5 border-white/10 backdrop-blur text-center" data-testid="signup-step-success">
            <div className="w-28 h-28 rounded-full bg-emerald-500/10 border-4 border-emerald-500 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-400"/>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-black uppercase mb-2 text-rose-500">Cadastro confirmado!</h1>
            <p className="text-white/70 mb-8 text-lg">Seu pagamento foi recebido e sua entrada já está liberada na catraca.</p>

            <div className="max-w-md mx-auto p-5 rounded-2xl bg-rose-950/30 border border-rose-500/30 mb-8">
              <div className="text-xs uppercase tracking-[0.3em] text-rose-400 font-bold mb-2">Bem-vindo</div>
              <div className="font-heading font-black text-3xl text-rose-500" data-testid="success-name">{form.name || "Aluno"}</div>
            </div>

            <Button
              onClick={() => { window.location.href = unitId ? `/totem/${unitId}` : "/totem"; }}
              className="w-full max-w-md h-16 bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600 text-white text-xl font-heading font-black uppercase rounded-2xl"
              data-testid="success-finish">
              <CheckCircle2 className="w-6 h-6 mr-2"/>Finalizar
            </Button>
            <div className="mt-4 text-white/50 text-sm" data-testid="success-countdown">
              Retornando ao totem em <strong className="text-rose-400 tabular-nums">{returnCountdown}s</strong>...
            </div>
          </Card>
        )}

        {/* Nav footer */}
        {step < 3 && (
          <div className="flex justify-between mt-6 max-w-3xl mx-auto">
            <Button variant="outline" onClick={back} disabled={step === 0} className="bg-transparent border-white/20 text-white hover:bg-white/10 disabled:opacity-30 font-bold uppercase" data-testid="signup-back"><ArrowLeft className="w-4 h-4 mr-2"/>Voltar</Button>
            {step < 2 ? (
              <Button onClick={next} className="bg-rose-600 hover:bg-rose-700 font-black uppercase" data-testid="signup-next">Continuar<ArrowRight className="w-4 h-4 ml-2"/></Button>
            ) : (
              <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 font-black uppercase" data-testid="signup-submit">
                {loading ? <Loader2 className="animate-spin w-4 h-4 mr-2"/> : <Sparkles className="w-4 h-4 mr-2"/>}
                Ir para o pagamento
              </Button>
            )}
          </div>
        )}
      </main>

      <footer className="px-8 py-6 text-center text-white/30 text-xs uppercase tracking-widest">
        American Fit · Autocadastro seguro
      </footer>
      {/* Mercado Pago Pix — site de vendas */}
      {form.plan_id && (
        <MpPixDialog
          open={mpPixOpen}
          onOpenChange={(v) => { setMpPixOpen(v); if (!v && step === 3 && payMethod === "pix") setPayMethod(null); }}
          plan={plans.find(p => p.id === form.plan_id)}
          createPayload={{
            plan_id: form.plan_id,
            payer_email: form.email || undefined,
            pending_student: {
              name: form.name, cpf: unmask(form.cpf), email: form.email, phone: unmask(form.phone),
              birthdate: isoFromBRDate(form.birthdate) || form.birthdate, gender: form.gender,
              zip_code: unmask(form.zip_code), address: form.address, address_number: form.address_number,
              address_complement: form.address_complement, neighborhood: form.neighborhood,
              city: form.city, state: form.state, unit_id: form.unit_id || null, plan_id: form.plan_id,
              portal_password: form.portal_password,
            },
          }}
          endpoints={{ create: "/public/mp/pix/create", status: "/portal/mp/pix/status/", sync: "/portal/mp/pix/sync/" }}
          onPaid={onMpPaid}
          title="Pagamento Pix — Mercado Pago"
        />
      )}
      {/* Mercado Pago Cartão — site de vendas */}
      {form.plan_id && (
        <MpCardDialog
          open={mpCardOpen}
          onOpenChange={(v) => { setMpCardOpen(v); if (!v && step === 3 && payMethod === "card") setPayMethod(null); }}
          plan={plans.find(p => p.id === form.plan_id)}
          payerEmail={form.email}
          payerCpf={unmask(form.cpf)}
          createPayload={{
            plan_id: form.plan_id,
            pending_student: {
              name: form.name, cpf: unmask(form.cpf), email: form.email, phone: unmask(form.phone),
              birthdate: isoFromBRDate(form.birthdate) || form.birthdate, gender: form.gender,
              zip_code: unmask(form.zip_code), address: form.address, address_number: form.address_number,
              address_complement: form.address_complement, neighborhood: form.neighborhood,
              city: form.city, state: form.state, unit_id: form.unit_id || null, plan_id: form.plan_id,
              portal_password: form.portal_password,
            },
          }}
          endpoints={{ create: "/public/mp/card/create", status: "/portal/mp/card/status/", sync: "/portal/mp/card/sync/" }}
          onPaid={onMpPaid}
          title="Pagar com cartão — Mercado Pago"
        />
      )}
    </div>
  );
}
